/**
 * ErrorList  component
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { FC, useCallback, useState } from "react";
import { BEM } from "@adapptio/lib-components";
import { Icon, ICON_SIZE } from "../Icon";
import { isNil } from "@adapptio/lib-shared";

export interface IItemListGroupProps {
	label: string;
	icon?: string;
	count?: number;
	collapsible?: boolean;
	isDefaultCollapsed?: boolean;
	className?: string;
}

export const ItemListGroup: FC<IItemListGroupProps> = ({
	label,
	icon,
	count,
	collapsible,
	isDefaultCollapsed,
	className,
	children
}) => {
	const [ isCollapsed, setIsCollapsed ] = useState(isDefaultCollapsed ?? false);

	const toggleGroup = useCallback(() => {
		setIsCollapsed((isCollapsed) => !isCollapsed);
	}, []);

	const baseClass = BEM("edt-item-list", "group", {
		"collapsible": collapsible
	});

	if (className) {
		baseClass.add(className);
	}

	return (
		<div className={baseClass.toClassName()}>
			<div className={baseClass.element("group-header").toClassName()} onClick={toggleGroup}>
				{ collapsible && isCollapsed && <Icon name="mdi/chevron-right" size={ICON_SIZE.SMALL} /> }
				{ collapsible && !isCollapsed && <Icon name="mdi/chevron-down" size={ICON_SIZE.SMALL} /> }
				{ icon && <Icon name={icon} size={ICON_SIZE.SMALL} />}
				<span className={baseClass.element("group-label").toClassName()}>{label}</span>
				{ !isNil(count) && <span className={baseClass.element("group-label-badge").toClassName()}>{count}</span> }
			</div>
			{
				collapsible && !isCollapsed && (
					<div className={baseClass.element("group-content").toClassName()}>
						{children}
					</div>
				)
			}
		</div>
	);
};
